/**
 * PLAT-11099 — what comes back from the chapterization prompt, made safe (DS8).
 *
 * The endpoint types its `result` as `unknown` because the output contract
 * belongs to the prompt record, so every assumption about the shape is checked
 * here rather than trusted. Nothing is written until the whole response has
 * survived this: a half-applied chapterization is the state E9 rules out.
 *
 * The governing judgement, from E6: **a boundary is worth more than its name,
 * and a Section is worth more than either.** So a malformed title costs the
 * title and keeps the chapter, a bad ordinal costs the ordinal, and a Section the
 * model forgot is kept rather than dropped — a dropped Section is a passage that
 * silently disappears from someone's video while the reel still looks plausible.
 *
 * Pure — no React, no store access, no network.
 */

/** A chapter to write: the Section it opens at, and its title where usable. */
export type ChapterBoundary = {
  firstSectionId: string;
  title?: string;
};

/** A chapter as the model proposed it: its title and the Sections it holds. */
export type ChapterGroup = {
  title?: string;
  sectionIds: string[];
};

export type ChapterizationResult = {
  boundaries: ChapterBoundary[];
  /**
   * PLAT-11154 — the same chapters as membership rather than as boundaries.
   * Boundaries are what gets written (membership is derived from position,
   * DE12), but a subset run has to ask whether a proposed chapter's Sections
   * even end up consecutive once the fixed ones are put back, and that question
   * cannot be asked of a boundary.
   */
  groups: ChapterGroup[];
  /**
   * PLAT-11153 — every Section the run was given, in the order the reel should
   * now play. Identical to the order sent when the run kept the order, so the
   * caller can apply it unconditionally or skip it by comparison; the boundaries
   * are always expressed against **this** order.
   */
  orderedSectionIds: string[];
};

/** Nothing usable came back. */
const NOTHING: ChapterizationResult = {
  boundaries: [],
  groups: [],
  orderedSectionIds: [],
};

/**
 * Over 60 characters is not a label any more (PLAT-11152's prompt says so), and
 * a heading that long breaks the row it renders in. The boundary survives; it
 * renders as its ordinal instead (PLAT-11055 DS5).
 */
const MAX_TITLE_CHARS = 60;

type ChapterEntry = { title?: unknown; sections?: unknown };

/**
 * Validate a response against the Sections it was asked about.
 *
 * @param result the endpoint's `result`, unvalidated
 * @param sectionIds the Sections in the order they were sent — the ordinals in
 *   the response index into this, 1-based
 * @param options `reorder` says the run asked the model to re-sequence the reel,
 *   which is the one thing that decides whether the returned order is read as an
 *   order or discarded as noise (DS9 / PLAT-11153 S2)
 * @returns the boundaries to write and the order to play them in, or empty when
 *   nothing is usable, in which case the caller fails the run and writes nothing
 */
export function chapterizationBoundaries(
  result: unknown,
  sectionIds: readonly string[],
  options?: { reorder?: boolean },
): ChapterizationResult {
  const entries = readEntries(result);
  if (entries.length === 0 || sectionIds.length === 0) return NOTHING;

  const seen = new Set<number>();
  // Each surviving entry as the ordinals it holds, still in the response's own
  // order — the sort into playback order happens once, below.
  const groups: { title?: string; ordinals: number[] }[] = [];

  for (const entry of entries) {
    const ordinals: number[] = [];
    for (const raw of Array.isArray(entry.sections) ? entry.sections : []) {
      const ordinal = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isInteger(ordinal)) continue;
      // Out of range is obviously wrong rather than plausibly wrong, which is
      // the property DS4 bought by sending ordinals instead of ids.
      if (ordinal < 1 || ordinal > sectionIds.length) continue;
      if (seen.has(ordinal)) continue;
      seen.add(ordinal);
      ordinals.push(ordinal);
    }
    // An entry naming no usable Section is not a chapter; it is a title with
    // nothing under it.
    if (ordinals.length === 0) continue;
    groups.push({ title: usableTitle(entry.title), ordinals });
  }

  if (groups.length === 0) return NOTHING;

  if (!options?.reorder) {
    // DS9 — the run asked for the current order, so a response whose ordinals
    // are not ascending is a model that did not comply. The correct reading is
    // "it grouped these Sections", not "it also wants them rearranged":
    // silently reshuffling a reel nobody asked to reshuffle is the worst
    // outcome available here. So each group is read as the set of positions it
    // claims, the boundary is taken from the earliest, and the reel's own order
    // is returned untouched.
    const ordered = groups
      .map(group => ({ ...group, opensAt: Math.min(...group.ordinals) }))
      .sort((a, b) => a.opensAt - b.opensAt);

    // A Section no entry mentioned is not dropped (E6). It belongs to whichever
    // chapter it now falls under, which needs no bookkeeping at all: membership
    // is derived from position (DE12), so leaving the boundaries alone puts it
    // in the chapter above it.
    return {
      boundaries: openAtFirstSection(
        ordered.map(group => boundaryAt(sectionIds[group.opensAt - 1], group)),
        sectionIds[0],
      ),
      groups: ordered.map(group => ({
        ...(group.title ? { title: group.title } : {}),
        // In reel order, since that is the order these chapters will play in.
        sectionIds: [...group.ordinals]
          .sort((a, b) => a - b)
          .map(ordinal => sectionIds[ordinal - 1]),
      })),
      orderedSectionIds: [...sectionIds],
    };
  }

  // PLAT-11153 — the response *is* the new running order, so the groups are
  // taken in the order they arrived and the ordinals inside each one in theirs.
  const orderedSectionIds = groups.flatMap(group =>
    group.ordinals.map(ordinal => sectionIds[ordinal - 1]),
  );

  // S6 — a Section no entry mentioned is appended in its original relative
  // order rather than dropped. Under keep-order that rule is defensive; here it
  // is load-bearing, because a forgotten ordinal would be a passage that
  // silently disappears from the creator's video while the reel still plays.
  const placed = new Set(orderedSectionIds);
  const forgotten = sectionIds.filter(id => !placed.has(id));
  orderedSectionIds.push(...forgotten);

  // Boundaries are expressed against the NEW order: a chapter opens at the
  // first of its own Sections as that order plays them. A group that came back
  // empty of usable ordinals is already gone, so every group here has one.
  const boundaries = groups.map(group =>
    boundaryAt(sectionIds[group.ordinals[0] - 1], group),
  );

  return {
    boundaries: openAtFirstSection(boundaries, orderedSectionIds[0]),
    groups: groups.map(group => ({
      ...(group.title ? { title: group.title } : {}),
      sectionIds: group.ordinals.map(ordinal => sectionIds[ordinal - 1]),
    })),
    orderedSectionIds,
  };
}

/**
 * PLAT-11153 — the Sections the run never saw, put back where they were.
 *
 * `reelChapterizationBaseText` leaves a Section out when it has nothing left to
 * read (every word removed or cut), so the ordinals the model answers in cover
 * only part of the reel. Harmless for the boundaries — membership is derived
 * from position (DE12) — but `orderedSectionIds` is handed to
 * `ReorderReelSectionsCommand`, which refuses anything that is not a permutation
 * of the **whole** reel. Without this, a reorder run on a reel holding one
 * wordless Section costs a round trip and then does nothing at all.
 *
 * An omitted Section is a fixed point: it keeps its own position, and the order
 * the model proposed flows through the positions that are left — the same rule
 * PLAT-11154 applies to a Section the creator did not tick.
 *
 * @param result the validated response, ordered over the Sections that were sent
 * @param allSectionIds every live Section, in the order the reel plays now
 */
export function restoreOmittedSections(
  result: ChapterizationResult,
  allSectionIds: readonly string[],
): ChapterizationResult {
  // Nothing was left out, or nothing usable came back to put back into.
  if (result.orderedSectionIds.length === allSectionIds.length) return result;
  if (result.boundaries.length === 0) return result;

  const sent = new Set(result.orderedSectionIds);
  let next = 0;
  const orderedSectionIds = allSectionIds.map(id =>
    sent.has(id) ? result.orderedSectionIds[next++] : id,
  );

  return {
    ...result,
    orderedSectionIds,
    // The reel's first Section may itself be one of the omitted ones, in which
    // case the boundary pulled onto the first *sent* Section now leaves the top
    // of the reel outside every chapter — the state DS3 rules out.
    boundaries: openAtFirstSection(result.boundaries, orderedSectionIds[0]),
  };
}

const boundaryAt = (
  firstSectionId: string,
  group: { title?: string },
): ChapterBoundary => ({
  firstSectionId,
  ...(group.title ? { title: group.title } : {}),
});

/**
 * PLAT-11055 DS3 — the reel's first Section always opens a chapter, or the
 * Sections above the first boundary sit outside every chapter, a state DE12 has
 * no slot for. Pulling the first boundary up is cheaper than dropping the whole
 * response over it, and it keeps that chapter's title.
 */
function openAtFirstSection(
  boundaries: ChapterBoundary[],
  firstSectionId: string,
): ChapterBoundary[] {
  return boundaries.map((boundary, index) =>
    index === 0 ? { ...boundary, firstSectionId } : boundary,
  );
}

/**
 * The endpoint hands back whatever the prompt produced: an array already parsed
 * server-side, or the raw text when it did not start with `[`. Both are handled,
 * including a model that fenced its output despite being told not to.
 */
function readEntries(result: unknown): ChapterEntry[] {
  if (Array.isArray(result)) {
    return result.filter(
      (entry): entry is ChapterEntry =>
        typeof entry === 'object' && entry !== null,
    );
  }

  if (typeof result !== 'string') return [];
  const cleaned = stripMarkdownFence(result).trim();
  if (!cleaned.startsWith('[')) return [];
  try {
    return readEntries(JSON.parse(cleaned));
  } catch {
    // Not JSON after all. A response the client cannot read is a failed run,
    // which the caller reports — it is not something to guess at.
    return [];
  }
}

/** The same tolerance `parsePromptResult` applies server-side. */
function stripMarkdownFence(raw: string): string {
  const fenced = raw.trim().match(/^```(?:[a-zA-Z]+)?\s*\n?([\s\S]*?)\n?```$/);
  return fenced ? fenced[1] : raw;
}

function usableTitle(title: unknown): string | undefined {
  if (typeof title !== 'string') return undefined;
  const trimmed = title.trim();
  if (!trimmed || trimmed.length > MAX_TITLE_CHARS) return undefined;
  return trimmed;
}
